import { useRef, useState } from 'react';
import { SwapVerticalOutlineIc } from '@dsch/dd-icons';

import { SimpleDropdown } from '../../../../components/Toolkit/SimpleDropdown/SimpleDropdown';
import { RadioButton } from '../../../../components/Toolkit/Inputs/RadioButton';
import ResponsiveDisplay from '../../../../features/MakeModelKeywordSearch/subcomponents/ResponsiveDisplay/ResponsiveDisplay';
import { AlwaysRenderedModal } from '../../../../features/MakeModelKeywordSearch/subcomponents/MakeModelKeywordSearchModal/AlwaysRenderedModal';

import { useSort } from '../../../../components/SearchPage/components/Sort/Sort.hook';
import type { SortProps } from '../../../../components/SearchPage/components/Sort/Sort.typed';
import * as SortStyle from '../../../../components/SearchPage/components/Sort/Sort.styled';
import { SortOptionsListbox } from '../../../../components/SearchPage/components/Sort/SortOptionsListbox';

const MOBILE_MODAL_TITLE = 'Sort by';

const Sort = (props: SortProps) => {
  const { displayLabel, items, selectSort } = useSort(props);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const modalContentRef = useRef<HTMLDivElement>(null);

  const handleSelect = (name: string) => {
    selectSort(name);
    setIsModalOpen(false);
  };

  const triggerContent = (
    <>
      <SwapVerticalOutlineIc width={20} height={20} aria-hidden="true" />
      <span>{displayLabel}</span>
    </>
  );

  return (
    <>
      <ResponsiveDisplay small={true} medium={false} large={false}>
        <SortStyle.MobileTrigger
          ref={triggerRef}
          type="button"
          onClick={() => setIsModalOpen(true)}
          aria-haspopup="dialog"
          aria-expanded={isModalOpen}
        >
          {triggerContent}
        </SortStyle.MobileTrigger>
        <AlwaysRenderedModal
          isOpen={isModalOpen}
          onRequestClose={() => setIsModalOpen(false)}
          contentLabel="Sort options"
          contentRef={modalContentRef}
          returnFocusTo={triggerRef}
        >
          <SortStyle.ModalHeader>
            <SortStyle.ModalHeaderTitle>
              {MOBILE_MODAL_TITLE}
            </SortStyle.ModalHeaderTitle>
            <SortStyle.ModalCloseButton
              type="button"
              onClick={() => setIsModalOpen(false)}
              aria-label="Close sort options"
            >
              <SortStyle.ModalHeaderChevron aria-hidden="true" />
            </SortStyle.ModalCloseButton>
          </SortStyle.ModalHeader>
          <SortStyle.ModalOptionList
            role="radiogroup"
            aria-label={MOBILE_MODAL_TITLE}
          >
            {items.map((item) => (
              <SortStyle.ModalOptionRow
                key={item.name || 'best-match'}
                data-tracking={item.dataTracking}
              >
                <RadioButton
                  name="sort"
                  value={item.name}
                  label={item.displayName}
                  checked={item.selected}
                  onChange={handleSelect}
                />
              </SortStyle.ModalOptionRow>
            ))}
          </SortStyle.ModalOptionList>
        </AlwaysRenderedModal>
      </ResponsiveDisplay>

      <ResponsiveDisplay small={false} medium={true} large={true}>
        <SimpleDropdown
          text={
            <SortStyle.DesktopTriggerContent>
              {triggerContent}
            </SortStyle.DesktopTriggerContent>
          }
          showArrow={false}
          popupRole="listbox"
        >
          <SortOptionsListbox options={items} onSelect={selectSort} />
        </SimpleDropdown>
      </ResponsiveDisplay>
    </>
  );
};

export { Sort };
